import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, LogOut, Star, Copy } from "lucide-react";
import { toast } from "sonner";
import { adminApi, setToken, formatApiError } from "@/lib/admin";
import { money, CATEGORY_LABELS, stockLabel } from "@/lib/site";

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    adminApi().get("/admin/products")
      .then((r) => setProducts(r.data))
      .catch((e) => {
        if (e.response?.status === 401) { setToken(null); nav("/admin/login"); }
        else toast.error("Failed to load products");
      });
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (p) => {
    try {
      await adminApi().put(`/admin/products/${p.id}`, { ...p, visible: !p.visible });
      toast.success(p.visible ? "Product hidden" : "Product visible", { description: p.name });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
    try {
      await adminApi().delete(`/admin/products/${p.id}`);
      toast.success("Product deleted", { description: p.name });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = async (p) => {
    try {
      const existingSlugs = new Set(products.map((x) => x.slug));
      let slug = `${p.slug}-copy`;
      let n = 2;
      while (existingSlugs.has(slug)) { slug = `${p.slug}-copy-${n}`; n += 1; }
      const payload = { ...p, slug, visible: false };
      delete payload.id;
      delete payload.effective_price;
      delete payload.created_at;
      delete payload.updated_at;
      if (!payload.sale_price) payload.sale_price = null;
      if (!payload.images || payload.images.length === 0) payload.images = p.image ? [p.image] : [];
      const { data } = await adminApi().post("/admin/products", payload);
      toast.success("Product duplicated (hidden)", { description: `${p.name} → copied as hidden. Review and save changes.` });
      nav(`/admin/edit/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <main className="min-h-screen bg-surface pt-28 pb-20" data-testid="admin-products-page">
      <div className="container-regal">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="overline-label mb-1">Admin</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Product Management</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/orders" data-testid="admin-orders-link" className="inline-flex items-center border border-linebright px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              Orders
            </Link>
            <Link to="/admin/kess3" data-testid="admin-kess3-options-link" className="inline-flex items-center border border-linebright px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              KESS3 Options
            </Link>
            <Link to="/admin/flex" data-testid="admin-flex-options-link" className="inline-flex items-center border border-linebright px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              FLEX Options
            </Link>
            <Link to="/admin/tuning-data" data-testid="admin-tuning-data-link" className="inline-flex items-center border border-linebright px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              Tuning Data
            </Link>
            <Link to="/admin/new" data-testid="admin-add-product-button" className="inline-flex items-center gap-2 bg-regal-amber px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
              <Plus size={15} /> Add Product
            </Link>
            <Link to="/" className="inline-flex items-center border border-linebright px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="admin-view-site">View Site</Link>
            <button onClick={() => { setToken(null); nav("/admin/login"); }} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-linebright px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-red hover:text-regal-red transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {!products ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 animate-pulse border border-line bg-white" />)}</div>
        ) : (
          <div className="overflow-x-auto border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="admin-products-table">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                  <th className="px-4 py-3.5">Product</th>
                  <th className="px-4 py-3.5">Category</th>
                  <th className="px-4 py-3.5">Price (ex VAT)</th>
                  <th className="px-4 py-3.5">Stock</th>
                  <th className="px-4 py-3.5 text-center">Featured</th>
                  <th className="px-4 py-3.5 text-center">Visible</th>
                  <th className="px-4 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className={`border-b border-line last:border-0 ${!p.visible ? "opacity-50" : ""}`} data-testid={`admin-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.image && <img src={p.image} alt="" className="h-11 w-11 border border-line object-cover" loading="lazy" />}
                        <div>
                          <p className="font-semibold text-ink">{p.name}</p>
                          <p className="font-mono text-[10px] text-neutral-500">{p.sku || p.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-400">{CATEGORY_LABELS[p.category] || p.category}</td>
                    <td className="px-4 py-3 font-mono text-ink">
                      {p.price_ex_vat > 0 ? money(p.effective_price ?? p.price_ex_vat) : "POA"}
                      {p.sale_price > 0 && <span className="ml-2 text-[10px] uppercase text-regal-red">sale</span>}
                    </td>
                    <td className="px-4 py-3 text-neutral-400">{stockLabel(p.stock)}</td>
                    <td className="px-4 py-3 text-center">{p.featured && <Star size={15} className="inline text-regal-amber" fill="currentColor" />}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => toggleVisible(p)} data-testid={`admin-toggle-visible-${p.slug}`} aria-label="Toggle visibility" className="text-neutral-400 hover:text-regal-amber transition-colors">
                        {p.visible ? <Eye size={17} /> : <EyeOff size={17} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => duplicate(p)} data-testid={`admin-duplicate-${p.slug}`} aria-label="Duplicate product" title="Duplicate product (copy is hidden by default)" className="grid h-9 w-9 place-items-center border border-line text-neutral-400 hover:border-regal-amber hover:text-regal-amber transition-colors"><Copy size={14} /></button>
                        <Link to={`/admin/edit/${p.id}`} data-testid={`admin-edit-${p.slug}`} aria-label="Edit product" className="grid h-9 w-9 place-items-center border border-line text-neutral-400 hover:border-regal-amber hover:text-regal-amber transition-colors"><Pencil size={14} /></Link>
                        <button onClick={() => remove(p)} data-testid={`admin-delete-${p.slug}`} aria-label="Delete product" className="grid h-9 w-9 place-items-center border border-line text-neutral-400 hover:border-regal-red hover:text-regal-red transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
